<template>
  <div
    class="colour-swatches"
    :class="[
      $blockClass(blockClass, 'colour-swatches'),
      {
        [$class('loading')]: state === 'loading',
        'colour-swatches--links': links
      }
    ]"
  >
    <component
      :is="links ? 'a' : 'button'"
      v-for="(colour, index) of limitedColours"
      :key="`${colour.colour}-${index}`"
      class="colour-swatches__swatch-container"
      :class="[
        $blockClass(blockClass, 'colour-swatch-container'),
        { [$class('active')]: activeColour.handle === colour.handle }
      ]"
      :href="links ? getHref(colour) : null"
      :tabindex="visible ? null : '-1'"
      :type="links ? null : 'button'"
      @click="links ? null : handleClick(colour)"
    >
      <span
        class="colour-swatches__swatch"
        :class="$blockClass(blockClass, 'colour-swatch')"
        :style="getStyles(colour)"
        v-text="$string('product.swatches.view', { replace: { colour: colour.label } })"
      />
    </component>

    <div
      v-if="limited && count > 0"
      class="colour-swatches__more text-body-2xs text-label-s-desktop"
      :class="{ [$class('loading')]: state === 'loading' }"
      v-text="$string('product.swatches.plus', { replace: { count } })"
    />
  </div>
</template>

<script>

/**
 * Vue: Colour swatches (colour-swatches)
 * -----------------------------------------------------------------------------
 * Linked product colour swatches used to change product.
 *
 * @param {String} [animationDelay] - Animation delay style.
 * @param {String} [blockClass] - Block level class to apply to all elements.
 * @param {Object} colours - Linked colour handles.
 * @param {String} handle - Current active handle.
 * @param {Boolean} [limited] - Limited number of colour swatches displayed.
 * @param {Boolean} [links] - Use links instead of radio inputs.
 * @param {Boolean} [visible] - Product card is visible in carousel.
 * @emits updateHandle - Emits handle when new colour swatch is selected.
 *
 */
import { convertHandleToSentenceCase, convertToHandle } from '~/helpers/convert'

export default {
  name: 'ColourSwatches',

  props: {
    animationDelay: {
      type: String,
      default: '0s',
    },
    blockClass: {
      type: String,
      default: '',
    },
    colours: {
      type: [Boolean, Object],
      default: () => ({}),
      required: true,
    },
    handle: {
      type: String,
      default: '',
      required: true,
    },
    limited: {
      type: Boolean,
      default: false,
    },
    links: {
      type: Boolean,
      default: false,
    },
    state: {
      type: String,
      default: 'ready',
    },
    visible: {
      type: Boolean,
      default: true,
    },
  },

  emits: {
    'update-handle': (payload) => typeof payload === 'string',
  },

  data() {
    return {
      activeColour: '',
      displayColours: [...Array(this.limit)].map(() => ({})),
      formattedColours: [],
      limit: cnvs.settings.colourSwatchesCount,
    }
  },

  computed: {

    /**
     * Compute count of excess products (when limited).
     * @returns {Number}
     */
    count() {
      return this.formattedColours?.length - this.limit
    },

    /**
     * Compute limited formatted colours.
     * @returns {Array{}}
     */
    limitedColours() {
      return this.limited
        ? this.displayColours?.slice(0, this.limit)
        : this.displayColours
    },
  },

  watch: {

    /**
     * Watch state to update display colours.
     */
    state() {
      if (this.state === 'loading') {
        this.displayColours = [...Array(this.limit)].map(() => ({}))
        return
      }

      this.displayColours = this.formattedColours
    },
  },

  mounted() {
    this.setFormattedColours()
    this.activeColour = this.formattedColours?.find((colour) => colour.active)
  },

  methods: {

    /**
     * Set formatted colours.
     * - Set on mounted instead of computing so it doesn't update when switching
     *   colours so initial active colour remains first.
     * - Formatted colours are separate from display colours so we can update
     *   the loading state without losing initial active colour position.
     */
    setFormattedColours() {
      this.formattedColours = Object.entries(this.colours)
        .map(([colour, handle]) => {
          const sanitisedColour = convertToHandle(colour)

          return {
            active: this.handle === handle,
            colour: sanitisedColour,
            handle,
            label: convertHandleToSentenceCase(sanitisedColour),
            url: cnvs.colours[sanitisedColour] ?? '',
          }
        })
        .sort((a, b) => {
          if (a.active) {
            return -1
          }

          if (b.active) {
            return 1
          }

          return 0
        })

      this.displayColours = this.formattedColours
    },

    /**
     * Get href attribute for links.
     * @param {Object} colour - Current colour object.
     * @returns {String}
     */
    getHref(colour) {
      return cnvs.page.collection
        ? `//${location.host}${cnvs.routes.normalisedRoot}collections/${cnvs.page.collection}/products/${colour.handle}`
        : `//${location.host}${cnvs.routes.normalisedRoot}products/${colour.handle}`
    },

    /**
     * Get styles object.
     * @param {Object} colour - Current colour object.
     * @param {Number} index - Colour index.
     * @returns {Object}
     */
    getStyles(colour) {
      if (this.state === 'loading') {
        return {
          animationDelay: this.animationDelay,
        }
      }

      const styles = {
        backgroundColor: colour.colour,
      }

      if (colour.url) {
        styles.backgroundImage = `url(${colour.url})`
      }

      return styles
    },

    /**
     * Handle swatch click.
     * @param {Object} colour - Colour object to load.
     */
    handleClick(colour) {
      if (!colour) {
        return
      }

      this.activeColour = colour
      this.$emit('update-handle', colour.handle)
    },
  },
}
</script>

<style lang="scss">
@import './colour-swatches';
</style>
